import React from 'react';
import Button from './Button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "primary" | "secondary" | "success" | "danger" | "warning";
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onCancel} />

        {/* Dialog centering */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        {/* Dialog panel */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className={`flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                confirmVariant === 'danger' ? 'bg-red-100' :
                confirmVariant === 'primary' ? 'bg-blue-100' :
                confirmVariant === 'success' ? 'bg-green-100' :
                confirmVariant === 'warning' ? 'bg-yellow-100' : 'bg-gray-100'
              }`}>
                <i className={`fas ${
                  confirmVariant === 'danger' ? 'fa-exclamation-triangle text-red-600' :
                  confirmVariant === 'primary' ? 'fa-info-circle text-blue-600' :
                  confirmVariant === 'success' ? 'fa-check-circle text-green-600' :
                  confirmVariant === 'warning' ? 'fa-exclamation-circle text-yellow-600' : 'fa-question-circle text-gray-600'
                }`}></i>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button
              variant={confirmVariant}
              onClick={onConfirm}
              isLoading={isLoading}
              className="sm:ml-3"
            >
              {confirmLabel}
            </Button>
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;